import { readonly, ref, isRef, ComputedRef, Ref } from '@nuxtjs/composition-api';
import { Logger } from '~/helpers/logger';
import type { GetProductSearchParams } from '~/modules/catalog/product/types';
import useApi from '~/composables/useApi';
import { sortingOptions, SortingOptionsValuesEnum } from '~/modules/catalog/category/composables/useFacet/sortingOptions';
import { perPageOptions } from '~/modules/catalog/category/composables/useFacet/perPageOptions';
import { createProductAttributeFilterInput } from '~/modules/catalog/category/composables/useFacet/input/createProductAttributeFilterInput';
import { createProductAttributeSortInput } from '~/modules/catalog/category/composables/useFacet/input/createProductAttributeSortInput';
import { Products } from '~/modules/GraphQL/types';
import getFacetDataQuery from './getFacetData.gql';
import getSearchDataQuery from './getSearchData.gql';

import type {
  UseFacetInterface, UseFacetErrors, UseFacetSearchResult, FacetSearchParams,
} from './useFacet';
import { useCategoryStore } from '../../stores/category';

export function useFacet(testingCategories: any[], currentCategoryId: string | number | Ref<any> | ComputedRef<any>): UseFacetInterface {
  const { query } = useApi();
  const categoryStore = useCategoryStore();
  
  const loading = ref(false);
  const error = ref<UseFacetErrors>({ search: null });
  
  // Inizializziamo con una struttura dati completa per evitare "readonly" durante l'assegnazione successiva
  const result = ref<UseFacetSearchResult>({ 
    data: {
      items: [],
      total: 0,
      availableSortingOptions: sortingOptions,
      perPageOptions,
      itemsPerPage: 20,
    }, 
    input: null 
  });

  const defaultItemsPerPage = 20;

  const search = async (params?: FacetSearchParams) => {
    Logger.debug('useFacet/search', params);

    try {
      loading.value = true;

      // 1. Risoluzione dinamica dell'ID categoria (gestisce Ref, Computed o Static)
      const resolvedCategoryId = isRef(currentCategoryId) 
        ? currentCategoryId.value 
        : currentCategoryId;

      const allCategories = categoryStore.rawCategories?.items[0]?.children ?? [];
      const pageSize = params.itemsPerPage ? params.itemsPerPage : defaultItemsPerPage;

 /**
       * relevanceSort has a SINGLE key, never one per active filter attribute ("doppia relevance" isn't
       * supported backend-side): it picks the perimeter of just ONE attribute, "tematiche" if it has any
       * active option - it's the catalog's primary browsing dimension, so it always wins, including when
       * several filters arrive together from a URL/shared link (no real click order to go by then).
       * Otherwise it falls back to the first filter the user actually applied, i.e. the one whose key was
       * inserted first in params.filters: selectFilter (useFilters.ts) only creates an attrCode's key the
       * first time an option of it is selected, so key order mirrors real click chronology. Emptying an
       * attribute (its key stays, with no values) is skipped, so removing every tematica option hands the
       * "primary" role to whichever filter (professioni, ...) was applied next.
       */
      const inputFilters = params?.filters ?? {};
      const isFilterActive = (attrCode: string) => !!(inputFilters[attrCode] ?? []).length;
      const primaryFilterAttrCode = isFilterActive('tematiche')
        ? 'tematiche'
        : Object.keys(inputFilters).find(isFilterActive);

      const relevanceSort = primaryFilterAttrCode ? { [`${primaryFilterAttrCode}_relevance`]: 'ASC' } : {};
          console.log('relevanceSort::::::', relevanceSort)


      // 2. Costruzione filtri per navigazione Categoria
      const productCategoryParams: GetProductSearchParams = {
        pageSize,
        search: '',
        filter: createProductAttributeFilterInput(params, true, { 
          currentCategoryId: resolvedCategoryId, 
          testingCategories, 
          allCategories 
        }),
        sort: Object.keys(relevanceSort).length<1 ? createProductAttributeSortInput(params.sort || SortingOptionsValuesEnum.POSITION_ASC) : relevanceSort,
        currentPage: params.page,
      };

      // 3. Logica per ricerca testuale (Search)
      let categoryIds = params?.ids || [];
      if (testingCategories?.length > 0) {
        categoryIds = categoryIds.filter(el => !testingCategories.includes(el));
      }

      const attributeFilterInput = createProductAttributeFilterInput(params, false, {
            currentCategoryId: resolvedCategoryId,
            testingCategories,
            allCategories
          })

      const searchDataSortInput = {...createProductAttributeSortInput(params.sort || ''), ...relevanceSort}

     
      const productSearchParams: GetProductSearchParams = {
        pageSize,
        search: params.term,
        currentPage: params.page,
        sort: searchDataSortInput,
        filter: { 
          category_id: { in: categoryIds }, 
          ...attributeFilterInput
        },
        ...(params.term ? {
          
        }:{})
      };

      const getCorrectQuery = params.term ? getSearchDataQuery : getFacetDataQuery;
      const getCorrectParams = params.term ? productSearchParams : productCategoryParams;



      // 4. Esecuzione Query
      const { data } = await query<{ products: Products }>(getCorrectQuery, getCorrectParams);
      const products = data?.products ?? null;

      // 5. AGGIORNAMENTO GRANULARE (Evita l'errore "computed is readonly")
      // Invece di result.value = { ... }, modifichiamo le proprietà dell'oggetto esistente
      result.value.input = params;
      result.value.data = {
        items: products?.items ?? [],
        total: products?.total_count ?? 0,
        availableSortingOptions: sortingOptions,
        perPageOptions,
        itemsPerPage: pageSize,
      };

      error.value.search = null;
    } catch (err) {
      error.value.search = err;
      Logger.error('useFacet/search', err);
    } finally {
      loading.value = false;
    }
  };

  return {
    search,
    result, // NON usare readonly() qui per permettere l'aggiornamento interno
    error: readonly(error),
    loading: readonly(loading),
  };
}

export * from './useFacet';
export default useFacet;